/**
 * Normalises a GET /poi_tracking response into what the redesign renders:
 * a point-of-impact view and a scene/background forensics panel.
 *
 * Shape notes that drive the parsing (see HEGIC_API.md):
 *  - `data.poi_result` is a JSON **string**, not an object.
 *  - Once parsed it holds the same POI block twice — at `poi_result` and at
 *    `poi_api_response.poi`. They agreed in every sample; the top-level one is
 *    preferred and the nested one is the fallback.
 *  - `poi_status` can be pending or failed, with the reason in `error_message`,
 *    so absence of a result is not the same as absence of the feature.
 *  - Part names here are hyphenated ("rear-bumper"), unlike the assessment
 *    API's spaced names ("Rear Bumper").
 */

const TIERS = ["primary", "secondary", "tertiary"];

const asArray = (v) => (Array.isArray(v) ? v : []);

// "rear-bumper" -> "Rear bumper"
export const humanisePart = (name) =>
  typeof name === "string" && name
    ? name
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, (c) => c.toUpperCase())
    : "";

/**
 * Collapse the per-image metadata into what a reviewer needs.
 *
 * Eight images that agree on every field would be eight identical cards, so the
 * agreed values are reported once as a consensus and only the disagreements and
 * raised flags are listed per image. That inverts the payload: the interesting
 * part is what is inconsistent.
 */
const buildForensics = (imageMetadata) => {
  const images = asArray(imageMetadata);
  if (!images.length) return null;

  // Distinct non-empty values for a (possibly nested) field.
  const distinct = (getter) => {
    const seen = new Set();
    images.forEach((img) => {
      const value = getter(img);
      if (value !== undefined && value !== null && value !== "") seen.add(value);
    });
    return [...seen];
  };

  const consensus = (label, getter) => {
    const values = distinct(getter);
    return {
      label,
      value: values.length === 1 ? values[0] : null,
      values,
      // More than one value is not an error — it is the thing worth showing.
      mixed: values.length > 1,
    };
  };

  const FLAG_LABELS = {
    background_vehicles: "Background vehicles",
    basement: "Basement / covered parking",
    cng_lpg_evidence: "CNG / LPG evidence",
    stickers_or_decals: "Stickers or decals",
  };

  // A flag matters only where it is true; count the images that raised it.
  const flags = Object.entries(FLAG_LABELS)
    .map(([key, label]) => {
      const raisedBy = images
        .filter((img) => img.validation_checks?.[key] === true)
        .map((img) => img.filename || img.image_id);
      return { key, label, raised: raisedBy.length > 0, raisedBy };
    })
    .sort((a, b) => Number(b.raised) - Number(a.raised));

  const obstructions = [
    ...new Set(images.flatMap((img) => asArray(img.obstructions))),
  ];

  const findings = [
    ...new Set(
      images.flatMap((img) => asArray(img.validation_checks?.findings)),
    ),
  ];

  const identity = images.find((img) => img.vehicle_identity)?.vehicle_identity;
  const inconsistentIdentity = images.filter(
    (img) =>
      img.vehicle_identity?.primary_vehicle_consistent &&
      img.vehicle_identity.primary_vehicle_consistent !== "yes",
  ).length;

  return {
    imageCount: images.length,

    consensus: [
      consensus("Setting", (i) => i.environment?.setting),
      consensus("Scene", (i) => i.environment?.scene),
      consensus("Time of day", (i) => i.time_of_day),
      consensus("Weather", (i) => i.weather),
      consensus("Exposure", (i) => i.exposure),
      consensus("Authenticity", (i) => i.authenticity?.value),
      consensus("Image quality", (i) => i.image_quality?.overall),
      consensus("Blur", (i) => i.image_quality?.blur),
      consensus("Cleanliness", (i) => i.vehicle_condition?.cleanliness),
    ],

    flags,
    obstructions,
    findings,

    identity: identity
      ? {
          make: identity.make,
          model: identity.model,
          numberPlate: identity.number_plate,
          consistent: inconsistentIdentity === 0,
          inconsistentCount: inconsistentIdentity,
        }
      : null,

    // Per-image, only what varies plus anything it flagged.
    perImage: images.map((img) => ({
      id: img.image_id || img.filename,
      filename: img.filename,
      viewSide: img.view_side,
      scene: img.environment?.scene,
      locationFinding: img.environment?.location_finding,
      authenticity: img.authenticity?.value,
      authenticityFinding: img.authenticity?.finding,
      quality: img.image_quality?.overall,
      blur: img.image_quality?.blur,
      lightingIssue: img.image_quality?.lighting_issue === true,
      issues: asArray(img.image_quality?.issues),
      obstructions: asArray(img.obstructions),
      conditionFinding: img.vehicle_condition?.finding,
      underRepair: img.vehicle_condition?.under_repair === true,
      dismantled: img.vehicle_condition?.dismantled === true,
      raisedFlags: Object.entries(FLAG_LABELS)
        .filter(([key]) => img.validation_checks?.[key] === true)
        .map(([, label]) => label),
    })),
  };
};

export const toPoiModel = (envelope) => {
  const data = envelope?.data;
  if (!data) return null;

  const status = data.poi_status || null;

  let parsed = null;
  if (typeof data.poi_result === "string" && data.poi_result) {
    try {
      parsed = JSON.parse(data.poi_result);
    } catch {
      // Keep going: poi_image and the status are still worth rendering.
      parsed = null;
    }
  } else if (data.poi_result && typeof data.poi_result === "object") {
    parsed = data.poi_result;
  }

  const api = parsed?.poi_api_response || {};
  const poi = parsed?.poi_result || api.poi || null;

  const tiers = poi
    ? TIERS.map((tier) => ({
        tier,
        parts: asArray(poi[`poi_${tier}`]).map(humanisePart),
      })).filter((entry) => entry.parts.length)
    : [];

  const coord = poi?.poi_coord || null;

  return {
    status,
    isComplete: status === "completed",
    errorMessage: data.error_message || null,
    requestedAt: data.created_at || null,
    completedAt: data.completed_at || null,

    // Annotated still the model chose as clearest evidence of the impact.
    image: data.poi_image || null,

    // Normalised 0–1 so a marker can be positioned by percentage and stay
    // correct at any rendered size. Pixel values are for the original image.
    marker:
      coord && Number.isFinite(coord.x_normalized) && Number.isFinite(coord.y_normalized)
        ? {
            xPct: coord.x_normalized * 100,
            yPct: coord.y_normalized * 100,
            x: coord.x,
            y: coord.y,
            imageWidth: coord.image_width,
            imageHeight: coord.image_height,
          }
        : null,

    confidence: poi?.confidence || null,
    explanation: poi?.poi_explanation || null,
    reviewRequired: poi?.review_required === true,
    tiers,

    partDecisions: asArray(poi?.poi_part_decisions).map((entry) => ({
      partName: humanisePart(entry.part_name),
      rawPartName: entry.part_name,
      tier: entry.impact_tier || null,
      decision: entry.decision || null,
      reviewRequired: entry.review_required === true,
    })),

    summary: api.damage_summary
      ? {
          headline: api.damage_summary.headline || null,
          description: api.damage_summary.description || null,
          confidence: api.damage_summary.confidence || null,
          focus: asArray(api.damage_summary.damage_focus),
          limitations: asArray(api.damage_summary.limitations),
        }
      : null,

    forensics: buildForensics(api.image_metadata),
  };
};

export default toPoiModel;
